/**
 * product.js — product page behavior.
 *
 * Ports (from the headless app):
 *  - components/product/product-context.tsx  -> single state object below
 *    (quantity, selected options, selected volume tier) + URL syncing
 *  - components/product/gallery.tsx           -> gallery (crossfade, thumbs,
 *    prev/next, ?image= param)
 *  - components/product/variant-selector.tsx  -> option buttons, availability
 *    matrix, ?variant= URL update, price refresh
 *  - components/product/product-description.tsx QuantitySelector
 *    + sticky-add-to-cart.tsx stepper          -> [data-qty-widget] contract
 *  - components/product/volume-discount-selector.tsx -> tier picker panel
 *    re-render + gift tiers fetched from window.themeConfig.giftsProxyPath
 *    (same response shape as lib/gifts.ts; degrades silently on failure)
 *  - components/product/sticky-add-to-cart.tsx -> IntersectionObserver on
 *    #main-add-to-cart; sticky-cart-spacer.tsx -> html.has-sticky-cart
 *  - RelatedProducts (page.tsx)                -> Shopify recommendations
 *    AJAX endpoint (limit=10, the endpoint max — the source rendered all
 *    Storefront API recommendations) rendered through the <template>
 *    markup in sections/product-related.liquid (last section on the page)
 *  - features/product/AccessoriesSection.tsx   -> mobile scroll-to-center
 *
 * Add-to-cart submission itself is handled by the shared <product-form>
 * custom element in assets/cart.js — this file only keeps the hidden
 * id/quantity inputs current.
 */
(function () {
  "use strict";

  if (window.__munchiesProductJs) return;
  window.__munchiesProductJs = true;

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Money formatting — uses the store money format (same output as the  */
  /* Liquid `money` filters used for the server-rendered initial paint). */
  /* ------------------------------------------------------------------ */
  function groupNumber(cents, decimals, thousands, decimal) {
    var units = (cents / 100).toFixed(decimals);
    var parts = units.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }

  function moneyWithFormat(cents, noDecimals) {
    var config = window.themeConfig || {};
    var format = config.moneyFormat || "{{amount}}";
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : "amount";
    var value;
    switch (key) {
      case "amount_no_decimals":
        value = groupNumber(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = groupNumber(cents, noDecimals ? 0 : 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = groupNumber(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = groupNumber(cents, noDecimals ? 0 : 2, "'", ".");
        break;
      default:
        value = groupNumber(cents, noDecimals ? 0 : 2, ",", ".");
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function formatMoney(cents) {
    return moneyWithFormat(Math.round(cents));
  }

  /** Port of showingPrice() — whole units, no decimals. */
  function formatMoneyWhole(cents) {
    return moneyWithFormat(Math.round(cents / 100) * 100, true);
  }

  /* ------------------------------------------------------------------ */
  /* Class toggling via data attributes (full class lists live in the    */
  /* .liquid markup so the Tailwind build always sees them).             */
  /* ------------------------------------------------------------------ */
  function tokens(el, attr) {
    return ((el && el.getAttribute(attr)) || "").split(/\s+/).filter(Boolean);
  }

  function applyStateClasses(el, selected) {
    var add = tokens(el, selected ? "data-selected-class" : "data-unselected-class");
    var remove = tokens(el, selected ? "data-unselected-class" : "data-selected-class");
    remove.forEach(function (c) {
      el.classList.remove(c);
    });
    add.forEach(function (c) {
      el.classList.add(c);
    });
  }

  function updateUrlParam(key, value) {
    try {
      var url = new URL(window.location.href);
      url.searchParams.set(key, value);
      window.history.replaceState({}, "", url.toString());
    } catch (e) {
      /* URL API unavailable — skip, purely cosmetic */
    }
  }

  /* ------------------------------------------------------------------ */
  /* AccessoriesSection: scroll the mobile carousel to the center card   */
  /* ------------------------------------------------------------------ */
  function initAccessoriesScroll() {
    var container = document.querySelector("[data-accessories-scroll]");
    if (!container) return;
    var center = container.querySelector("[data-accessories-center]");
    if (!center) return;
    var scrollPosition = center.offsetLeft - container.offsetWidth / 2 + center.offsetWidth / 2;
    container.scrollTo({ left: scrollPosition, behavior: "smooth" });
  }

  function initProductSection(root) {
    if (!root || root.__productInit) return;
    root.__productInit = true;

    /* Port of StickyCartSpacer — footer padding via html class (CSS exists). */
    document.documentElement.classList.add("has-sticky-cart");

    var dataEl = root.querySelector("[data-product-data]");
    if (!dataEl) return;
    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var volume = null;
    var volumeEl = root.querySelector("[data-volume-tiers]");
    if (volumeEl) {
      try {
        volume = JSON.parse(volumeEl.textContent);
      } catch (e) {
        volume = null;
      }
    }

    var GRAMS_PER_BAG = 50;
    var GRAMS_PER_DAY = 2;

    /* ---------------------------- state ----------------------------- */
    function variantById(id) {
      for (var i = 0; i < data.variants.length; i++) {
        if (String(data.variants[i].id) === String(id)) return data.variants[i];
      }
      return null;
    }

    function findVariant(selection) {
      return (
        data.variants.find(function (variant) {
          return data.options.every(function (option) {
            return variant.options[option.name] === selection[option.name];
          });
        }) || null
      );
    }

    var initialVariant = variantById(data.selectedVariantId) || data.variants[0] || null;
    var state = {
      quantity: data.initialQuantity || 1,
      variant: initialVariant,
      selectedOptions: {},
      giftTiers: null,
    };
    if (initialVariant) {
      data.options.forEach(function (option) {
        state.selectedOptions[option.name] = initialVariant.options[option.name];
      });
    }

    /** Active volume tier for the current quantity (highest tier <= qty). */
    function activeTier() {
      if (!volume || !volume.tiers || !volume.tiers.length) return null;
      var qty = Math.max(1, state.quantity);
      var best = null;
      volume.tiers.forEach(function (tier) {
        if (tier.quantity <= qty && (!best || tier.quantity > best.quantity)) {
          best = tier;
        }
      });
      return best;
    }

    /** Unit price in cents — discounted when a volume tier is active. */
    function currentUnitCents() {
      var tier = activeTier();
      if (tier) return tier.unit;
      return state.variant ? state.variant.price : volume ? volume.basePrice : 0;
    }

    /* --------------------------- renders ---------------------------- */
    function renderQuantityInputs() {
      root.querySelectorAll("[data-qty-widget] [data-qty-input]").forEach(function (input) {
        if (document.activeElement !== input) {
          input.value = String(state.quantity);
        }
      });
    }

    function renderForms() {
      root.querySelectorAll("[data-form-quantity]").forEach(function (input) {
        input.value = String(state.quantity);
      });
      if (state.variant) {
        root.querySelectorAll("[data-form-variant-id]").forEach(function (input) {
          input.value = String(state.variant.id);
        });
      }
    }

    function currencySuffix() {
      return '<span class="ml-1 inline">' + data.currency + "</span>";
    }

    /* Price block (non-matcha products) — port of the Price rows. */
    function renderPriceBlock() {
      var block = root.querySelector("[data-price-block]");
      if (!block) return;
      var tier = activeTier();
      var currentCents = tier ? Math.round(tier.unit) : state.variant ? state.variant.price : 0;
      var compareCents = tier
        ? volume.basePrice
        : state.variant && state.variant.compareAtPrice
          ? state.variant.compareAtPrice
          : 0;
      var priceEl = block.querySelector("[data-price]");
      if (priceEl) priceEl.innerHTML = formatMoney(currentCents) + currencySuffix();
      var compareEl = block.querySelector("[data-compare-price]");
      if (compareEl) {
        if (compareCents > currentCents) {
          compareEl.innerHTML = formatMoney(compareCents) + currencySuffix();
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    function renderStickyPrice() {
      var el = root.querySelector("[data-sticky-price]");
      if (!el || !state.variant) return;
      el.innerHTML = formatMoney(state.variant.price) + currencySuffix();
    }

    function renderAtcPrice() {
      var el = root.querySelector("[data-atc-price]");
      if (el) {
        el.textContent = formatMoney(Math.round(currentUnitCents() * state.quantity));
      }
      // Notify add-on widgets (e.g. the Seal Subscriptions quantity sync) of any
      // price-affecting change — quantity, variant or initial render.
      document.dispatchEvent(new CustomEvent("product:price-change"));
    }

    function renderAtcLabels() {
      var hasVariant = !!state.variant;
      [
        ["[data-atc-button]", "[data-atc-label]"],
        ["[data-sticky-submit]", "[data-sticky-label]"],
      ].forEach(function (pair) {
        var button = root.querySelector(pair[0]);
        if (!button) return;
        var label = button.querySelector(pair[1]);
        if (label) {
          label.textContent = hasVariant
            ? button.getAttribute("data-label-add")
            : button.getAttribute("data-label-select");
        }
        button.disabled = !hasVariant;
      });
    }

    function renderStockState() {
      var available = !!(state.variant && state.variant.available);
      var toggles = [
        ["[data-stock-pill-available]", available],
        ["[data-stock-pill-unavailable]", !available],
        ["[data-stock-message-available]", available],
        ["[data-stock-message-unavailable]", !available],
      ];
      toggles.forEach(function (pair) {
        var el = root.querySelector(pair[0]);
        if (el) el.hidden = !pair[1];
      });
    }

    /* Volume panel — port of the PRICE PANEL V2 + tier card selection. */
    function renderVolume() {
      var holder = root.querySelector("[data-volume-discounts]");
      if (!holder || !volume) return;

      var qty = Math.max(1, state.quantity);
      var tier = activeTier();
      var unit = tier ? tier.unit : volume.basePrice;
      var hasFreeShipping = !!tier;
      var overFive = qty > 5;

      var freeShipping = holder.querySelector("[data-free-shipping]");
      if (freeShipping) freeShipping.hidden = !hasFreeShipping;

      var total = holder.querySelector("[data-vd-total]");
      if (total) total.textContent = formatMoney(Math.round(unit * qty));

      var compare = holder.querySelector("[data-vd-compare]");
      if (compare) {
        compare.textContent = formatMoney(volume.basePrice * qty);
        compare.hidden = !tier;
      }

      var perDayRow = holder.querySelector("[data-vd-stats-perday]");
      if (perDayRow) perDayRow.hidden = overFive;
      var perDay = holder.querySelector("[data-vd-perday]");
      if (perDay) perDay.textContent = formatMoney(Math.round(unit / (GRAMS_PER_BAG / GRAMS_PER_DAY)));

      var supplyRow = holder.querySelector("[data-vd-stats-supply]");
      if (supplyRow) supplyRow.hidden = overFive;
      var supply = holder.querySelector("[data-vd-supply]");
      if (supply) {
        var template = supply.getAttribute("data-template") || "__DAYS__";
        var days = (qty * GRAMS_PER_BAG) / GRAMS_PER_DAY;
        supply.textContent = template.replace("__DAYS__", String(days));
      }

      var grams = holder.querySelector("[data-vd-grams]");
      if (grams) grams.textContent = String(qty * GRAMS_PER_BAG);

      /* Tier card selection states */
      var activeQuantity = tier ? tier.quantity : 1;
      holder.querySelectorAll("[data-volume-tier]").forEach(function (card) {
        var selected = Number(card.getAttribute("data-quantity")) === activeQuantity;
        applyStateClasses(card, selected);
        var radio = card.querySelector("[data-tier-radio]");
        if (radio) applyStateClasses(radio, selected);
        var dot = card.querySelector("[data-tier-radio-dot]");
        if (dot) dot.hidden = !selected;
        var count = card.querySelector("[data-tier-count]");
        if (count) applyStateClasses(count, selected);
        var price = card.querySelector("[data-tier-price]");
        if (price) applyStateClasses(price, selected);
      });

      renderGiftArea();
    }

    /* ------------------------- gift tiers --------------------------- */
    /* Display port of the gift area in volume-discount-selector.tsx.    */
    function renderGiftArea() {
      var holder = root.querySelector("[data-gift-area]");
      if (!holder) return;
      var giftTiers = state.giftTiers;
      if (!giftTiers || !giftTiers.length) {
        holder.hidden = true;
        holder.innerHTML = "";
        return;
      }

      var qty = Math.max(1, state.quantity);
      holder.innerHTML = "";
      holder.hidden = false;

      var activeGiftTier = giftTiers.find(function (tier) {
        return qty >= tier.minQuantity && (tier.maxQuantity == null || qty <= tier.maxQuantity);
      });

      if (activeGiftTier) {
        var template = root.querySelector("[data-gift-active-template]");
        var chipTemplate = root.querySelector("[data-gift-chip-template]");
        if (!template) return;
        var panel = template.content.firstElementChild.cloneNode(true);
        var chipsRow = panel.querySelector("[data-gift-chips-row]");
        (activeGiftTier.gifts || []).forEach(function (gift) {
          if (!chipTemplate || !chipsRow) return;
          var chip = chipTemplate.content.firstElementChild.cloneNode(true);
          chip.textContent = gift.title || "Free Gift";
          chipsRow.appendChild(chip);
        });
        var totalValue = (activeGiftTier.gifts || []).reduce(function (sum, gift) {
          var parsed = parseFloat(gift.price || "0");
          return sum + (isNaN(parsed) ? 0 : parsed);
        }, 0);
        var valueEl = panel.querySelector("[data-gift-value]");
        if (valueEl && totalValue > 0) {
          var giftValueTemplate = (data.strings && data.strings.giftValue) || "__VALUE__";
          valueEl.textContent = giftValueTemplate.replace("__VALUE__", formatMoney(Math.round(totalValue * 100)));
          valueEl.hidden = false;
        }
        holder.appendChild(panel);
      } else {
        /* Pulsing teasers under tiers that unlock gifts */
        var teaserTemplate = root.querySelector("[data-gift-teaser-template]");
        if (!teaserTemplate) return;
        var cardQuantities = [1];
        (volume && volume.tiers ? volume.tiers : []).forEach(function (tier) {
          cardQuantities.push(tier.quantity);
        });
        var grid = document.createElement("div");
        grid.className = "grid grid-cols-3 gap-2";
        var anyTeaser = false;
        cardQuantities.forEach(function (tierQuantity) {
          var hasGift = giftTiers.some(function (giftTier) {
            return (
              tierQuantity >= giftTier.minQuantity &&
              (giftTier.maxQuantity == null || tierQuantity <= giftTier.maxQuantity)
            );
          });
          if (hasGift) {
            grid.appendChild(teaserTemplate.content.firstElementChild.cloneNode(true));
            anyTeaser = true;
          } else {
            grid.appendChild(document.createElement("div"));
          }
        });
        if (anyTeaser) {
          holder.appendChild(grid);
        } else {
          holder.hidden = true;
        }
      }
    }

    /* Fetch gift rules from the gifts app proxy. Degrades silently. */
    function fetchGifts() {
      var config = window.themeConfig || {};
      if (!config.giftsEnabled || !config.giftsProxyPath) return;
      if (!root.querySelector("[data-gift-area]")) return;
      fetch(config.giftsProxyPath + "?productIds=" + data.gid)
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (json) {
          if (!json || !json.rules) return;
          var rule = json.rules[data.gid];
          if (!rule || !rule.enabled || !rule.tiers || !rule.tiers.length) return;
          state.giftTiers = rule.tiers;
          renderGiftArea();
          /* Free shipping copy switches to "+ GIFTS" once gifts exist */
          var textEl = root.querySelector("[data-free-shipping-text]");
          if (textEl) {
            textEl.textContent = textEl.getAttribute("data-text-gifts") || textEl.textContent;
          }
        })
        .catch(function () {
          /* gifts are an optional enhancement — never break the page */
        });
    }

    /* ----------------------- variant picker ------------------------- */
    function renderVariantButtons() {
      var picker = root.querySelector("[data-variant-picker]");
      if (!picker) return;
      var activeClass = "data-active-class";
      var availableClass = "data-available-class";
      var unavailableClass = "data-unavailable-class";
      var allStateTokens = tokens(picker, activeClass)
        .concat(tokens(picker, availableClass))
        .concat(tokens(picker, unavailableClass));

      picker.querySelectorAll("[data-option-group]").forEach(function (group) {
        var optionName = group.getAttribute("data-option-name");
        group.querySelectorAll("[data-option-value]").forEach(function (button) {
          var value = button.getAttribute("data-option-value");

          /* Candidate selection: current selection with this value swapped in
             (port of the optionParams/filtered logic in variant-selector.tsx). */
          var candidate = {};
          Object.keys(state.selectedOptions).forEach(function (key) {
            candidate[key] = state.selectedOptions[key];
          });
          candidate[optionName] = value;

          var isAvailable = data.variants.some(function (variant) {
            return (
              variant.available &&
              Object.keys(candidate).every(function (key) {
                return variant.options[key] === candidate[key];
              })
            );
          });
          var isActive = state.selectedOptions[optionName] === value;

          allStateTokens.forEach(function (token) {
            button.classList.remove(token);
          });
          var stateAttr = !isAvailable ? unavailableClass : isActive ? activeClass : availableClass;
          tokens(picker, stateAttr).forEach(function (token) {
            button.classList.add(token);
          });
          button.disabled = !isAvailable;
          if (isAvailable) {
            button.removeAttribute("aria-disabled");
          } else {
            button.setAttribute("aria-disabled", "true");
          }
          var oosLabel = button.querySelector("[data-oos-label]");
          if (oosLabel) oosLabel.hidden = isAvailable;
        });
      });
    }

    function selectOption(name, value) {
      state.selectedOptions[name] = value;
      state.variant = findVariant(state.selectedOptions);
      if (state.variant) {
        updateUrlParam("variant", state.variant.id);
      }
      renderVariantButtons();
      renderPriceBlock();
      renderStickyPrice();
      renderAtcPrice();
      renderAtcLabels();
      renderStockState();
      renderForms();
    }

    /* -------------------------- quantity ---------------------------- */
    function setQuantity(quantity) {
      var next = Math.max(1, quantity);
      state.quantity = next;
      renderQuantityInputs();
      renderForms();
      renderVolume();
      renderPriceBlock();
      renderAtcPrice();
    }

    root.querySelectorAll("[data-qty-widget]").forEach(function (widget) {
      var input = widget.querySelector("[data-qty-input]");
      var minus = widget.querySelector("[data-qty-minus]");
      var plus = widget.querySelector("[data-qty-plus]");
      if (minus) {
        minus.addEventListener("click", function () {
          setQuantity(state.quantity - 1);
        });
      }
      if (plus) {
        plus.addEventListener("click", function () {
          setQuantity(state.quantity + 1);
        });
      }
      if (input) {
        input.addEventListener("focus", function () {
          setTimeout(function () {
            input.select();
          }, 0);
        });
        input.addEventListener("input", function () {
          /* Allow empty string for deletion, or digits only */
          input.value = input.value.replace(/[^\d]/g, "");
        });
        input.addEventListener("keydown", function (event) {
          if (event.key === "Enter") input.blur();
        });
        input.addEventListener("blur", function () {
          var parsed = parseInt(input.value, 10);
          if (!isNaN(parsed) && parsed >= 1) {
            setQuantity(parsed);
          } else {
            input.value = String(state.quantity);
          }
        });
      }
    });

    /* Volume tier cards — clicking selects the tier's quantity */
    root.querySelectorAll("[data-volume-tier]").forEach(function (card) {
      card.addEventListener("click", function () {
        var quantity = Number(card.getAttribute("data-quantity")) || 1;
        setQuantity(quantity);
      });
    });

    /* Variant option buttons */
    root.querySelectorAll("[data-option-group]").forEach(function (group) {
      var optionName = group.getAttribute("data-option-name");
      group.addEventListener("click", function (event) {
        var button = event.target.closest("[data-option-value]");
        if (!button || button.disabled) return;
        event.preventDefault();
        selectOption(optionName, button.getAttribute("data-option-value"));
      });
    });

    /* --------------------------- gallery ---------------------------- */
    (function initGallery() {
      var gallery = root.querySelector("[data-product-gallery]");
      if (!gallery) return;
      var slides = Array.prototype.slice.call(gallery.querySelectorAll("[data-gallery-slide]"));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll("[data-gallery-thumb]"));
      if (!slides.length) return;
      var index = 0;
      var transitioning = false;

      function applyIndex(next) {
        slides.forEach(function (slide, i) {
          if (i === next) {
            slide.classList.remove("opacity-0", "pointer-events-none");
            slide.classList.add("opacity-100");
            slide.removeAttribute("aria-hidden");
          } else {
            slide.classList.remove("opacity-100");
            slide.classList.add("opacity-0", "pointer-events-none");
            slide.setAttribute("aria-hidden", "true");
          }
        });
        thumbs.forEach(function (thumb) {
          var isActive = Number(thumb.getAttribute("data-index")) === next;
          tokens(thumb, isActive ? "data-inactive-class" : "data-active-class").forEach(function (c) {
            thumb.classList.remove(c);
          });
          tokens(thumb, isActive ? "data-active-class" : "data-inactive-class").forEach(function (c) {
            thumb.classList.add(c);
          });
        });
        index = next;
      }

      /* Port of handleImageChange — fade out 200ms, then swap + fade in. */
      function changeImage(next) {
        if (transitioning || next === index || next < 0 || next >= slides.length) return;
        transitioning = true;
        var current = slides[index];
        current.classList.remove("opacity-100");
        current.classList.add("opacity-0");
        setTimeout(function () {
          applyIndex(next);
          updateUrlParam("image", String(next));
          transitioning = false;
        }, 200);
      }

      var prev = gallery.querySelector("[data-gallery-prev]");
      var next = gallery.querySelector("[data-gallery-next]");
      if (prev) {
        prev.addEventListener("click", function () {
          changeImage(index === 0 ? slides.length - 1 : index - 1);
        });
      }
      if (next) {
        next.addEventListener("click", function () {
          changeImage(index + 1 < slides.length ? index + 1 : 0);
        });
      }
      thumbs.forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          changeImage(Number(thumb.getAttribute("data-index")));
        });
      });

      /* Initial index from the ?image= URL param (gallery.tsx state.image) */
      try {
        var param = new URL(window.location.href).searchParams.get("image");
        var initial = param === null ? NaN : parseInt(param, 10);
        if (!isNaN(initial) && initial > 0 && initial < slides.length) {
          applyIndex(initial);
        }
      } catch (e) {
        /* ignore */
      }
    })();

    /* ----------------------- sticky cart bar ------------------------ */
    (function initStickyBar() {
      var bar = root.querySelector("[data-sticky-cart]");
      var target = document.getElementById("main-add-to-cart");
      if (!bar || !target || !("IntersectionObserver" in window)) return;
      var visibleTokens = tokens(bar, "data-visible-class");
      var hiddenTokens = tokens(bar, "data-hidden-class");

      function setVisible(visible) {
        (visible ? hiddenTokens : visibleTokens).forEach(function (c) {
          bar.classList.remove(c);
        });
        (visible ? visibleTokens : hiddenTokens).forEach(function (c) {
          bar.classList.add(c);
        });
      }

      new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          /* Show when the main button is above the viewport (scrolled past) */
          setVisible(!!entry && !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0, rootMargin: "0px" },
      ).observe(target);
    })();

    /* ----------------- accordions (single open group) --------------- */
    root.querySelectorAll("[data-accordion-group]").forEach(function (group) {
      group.addEventListener(
        "toggle",
        function (event) {
          var details = event.target;
          if (!details.matches || !details.matches("details[data-accordion]") || !details.open) return;
          group.querySelectorAll("details[data-accordion][open]").forEach(function (other) {
            if (other !== details) other.open = false;
          });
        },
        true,
      );
    });

    /* ----------------------- related products ----------------------- */
    (function initRelated() {
      /* The related-products markup lives in its own section
         (sections/product-related.liquid, placed last in the template like
         the source page), so look it up document-wide; the endpoint URL
         stays on the main-product root. Early-returns keep pages without
         the container safe. */
      var holder = document.querySelector("[data-related-products]");
      var baseUrl = root.getAttribute("data-recommendations-url");
      var cardTemplate = document.querySelector("[data-related-card-template]");
      var wrapperTemplate = document.querySelector("[data-related-mobile-wrapper-template]");
      if (!holder || !baseUrl || !cardTemplate) return;
      /* Guard so an editor re-render of the main-product section doesn't
         re-fetch and duplicate cards into the (separate) related section. */
      if (holder.__relatedLoaded) return;
      holder.__relatedLoaded = true;
      var mobile = holder.querySelector("[data-related-mobile]");
      var desktop = holder.querySelector("[data-related-desktop]");

      function buildCard(productData, position) {
        var card = cardTemplate.content.firstElementChild.cloneNode(true);
        card.style.animationDelay = position * 50 + "ms";
        var link = card.querySelector("[data-card-link]");
        if (link) link.href = productData.url;
        var title = card.querySelector("[data-card-title]");
        if (title) title.textContent = productData.title;
        var price = card.querySelector("[data-card-price]");
        if (price) price.innerHTML = formatMoney(productData.price) + currencySuffix();
        var image = card.querySelector("[data-card-image]");
        if (image) {
          if (productData.featured_image) {
            var src = productData.featured_image;
            image.src = src + (src.indexOf("?") >= 0 ? "&" : "?") + "width=720";
            image.alt = productData.title;
          } else {
            image.hidden = true;
          }
        }
        return card;
      }

      fetch(baseUrl + ".json?product_id=" + encodeURIComponent(data.id) + "&limit=10&intent=related")
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (json) {
          var products = (json && json.products) || [];
          if (!products.length) return; /* stays hidden, like the source */
          products.forEach(function (productData, position) {
            if (desktop) desktop.appendChild(buildCard(productData, position));
            if (mobile) {
              var wrapper = wrapperTemplate
                ? wrapperTemplate.content.firstElementChild.cloneNode(true)
                : document.createElement("div");
              wrapper.appendChild(buildCard(productData, position));
              mobile.appendChild(wrapper);
            }
          });
          holder.hidden = false;
          if (window.themeUtils && window.themeUtils.refreshAnimations) {
            window.themeUtils.refreshAnimations(holder);
          }
        })
        .catch(function () {
          /* recommendations are an enhancement — keep the block hidden */
        });
    })();

    /* --------------------------- initial ---------------------------- */
    renderVariantButtons();
    renderForms();
    renderVolume();
    renderAtcPrice();
    fetchGifts();
  }

  ready(function () {
    initAccessoriesScroll();
    initProductSection(document.querySelector("[data-main-product]"));
  });

  /* Theme editor: rebind the product section after it is re-rendered.
     Called by theme.js on shopify:section:load (editor-only — that event never
     fires live, and section <script src> tags don't re-execute on re-render,
     so the live add-to-cart/state path is untouched). Idempotent via
     root.__productInit, so re-running over an unchanged subtree is a no-op. */
  window.themeProduct = {
    reinit: function (scope) {
      scope = scope || document;
      if (!scope.querySelectorAll) return;
      if (
        (scope.matches && scope.matches("[data-accessories-scroll]")) ||
        scope.querySelector("[data-accessories-scroll]")
      ) {
        initAccessoriesScroll();
      }
      var mp =
        scope.matches && scope.matches("[data-main-product]")
          ? scope
          : scope.querySelector("[data-main-product]");
      if (mp) initProductSection(mp);
    },
  };
})();
